import React, {  useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink, useLocation } from "react-router-dom";
import { useSidebar } from "../providers/SidebarContext";
import { useAuth, Roles } from "../providers/AuthContext";
import { getActingOrganizationPublicId } from "../helpers/actingOrganization";

import { useEffect } from "react";
import {
    faChevronDown,
    faChevronRight,
    faGears,
    faGear,
    faFolderTree,
    faTruckBolt,
    faCalendar,
    faChartColumn,
    faChartLine,
    faFaceSmile,
    faChargingStation,
    faSitemap,
    faPlug,
    faTag,
    faBadgeDollar,
    faUserGroup,
    faHandshake,
    faKey,
    faInfoCircle,
    faLinkHorizontal,
    faFileInvoice,
    faIndustryWindows,
    faBolt,
    faScrewdriverWrench,
    faBuilding,
    faComments
} from "@fortawesome/pro-regular-svg-icons";
 
export default function Sidebar() {
    const { isSidebarOpen } = useSidebar();
    const { t } = useTranslation();

    return (
        <div>
            <aside
                className={`fixed left-0 top-[70px] z-50 w-[16rem] shrink-0 h-[calc(100vh-70px)]
    overflow-y-auto bg-primary-500
    transform transition-transform duration-300
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
    md:translate-x-0 md:static pt-6`}
            >

                <ul className="px-2 space-y-0">
                    <li><SidebarItem requiredRoles={[Roles.Admin, Roles.CustomerService, Roles.Partner, Roles.Customer, Roles.CpmsLight]}
                        icon={<FontAwesomeIcon icon={faTruckBolt} className="text-mute-400 w-4" />}
                        text={t("nav.sessions")}
                        to="/"
                    /></li>

                    <li><SidebarItem requiredRoles={[Roles.Admin, Roles.CustomerService, Roles.Partner, Roles.ServicePartner, Roles.CpmsLight]}
                        icon={<FontAwesomeIcon icon={faBolt} className="text-mute-400 w-4" />}
                        text={t("nav.chargeMonitor")}
                        to="/chargeMonitor"
                    /></li>

                    <li><SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                        icon={<FontAwesomeIcon icon={faInfoCircle} className="text-mute-400 w-4" />}
                        text={t("nav.logs")}
                        to="/logs"
                    /></li>

                    <li><SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight, Roles.Partner]}
                        icon={<FontAwesomeIcon icon={faChartColumn} className="text-mute-400 w-4" />}
                        text={t("nav.siteStats")}
                        to="/SiteStatisticsDashboard"
                    /></li>

                    <li><SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                        icon={<FontAwesomeIcon icon={faChartLine} className="text-mute-400 w-4" />}
                        text={t("nav.customerStatistics")}
                        to="/customer-statistics"
                    /></li>

                    <SidebarItem requiredRoles={[Roles.Admin]}
                        icon={<FontAwesomeIcon icon={faCalendar} className="text-mute-400 w-4" />}
                        text={t("nav.booking")}
                        to="/booking" />

                    <li>
                        <SidebarItem
                            requiredRoles={[Roles.Admin]}
                            icon={<FontAwesomeIcon icon={faBadgeDollar} className="text-mute-400 w-4" />}
                            text={t("nav.electricityTrading")}
                            to=""
                            expandOnly={true}
                            matching={["grid-calculations", "grid-areas", "forecast-follow-up"]}
                            platformOnly
                        >
                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faChartColumn} className="text-mute-400 w-4" />}
                                text={t("nav.siteCosts")}
                                to="/grid-calculations"
                            />
                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faBolt} className="text-mute-400 w-4" />}
                                text={t("nav.gridAreas")}
                                to="/grid-areas"
                            />
                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faChartLine} className="text-mute-400 w-4" />}
                                text={t("nav.forecast")}
                                to="/forecast-follow-up"
                            />

                        </SidebarItem>
                    </li>
   

                    <li>
                        <SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                            icon={<FontAwesomeIcon icon={faSitemap} className="text-mute-400 w-4" />}
                            text={t("nav.infrastructure")}
                            to=""
                            expandOnly={true}
                            matching={["station", "point", "connector", "manufacturer", "sites", "tariffs", "site"]}
                        >
                            <SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]} icon={<FontAwesomeIcon icon={faFolderTree} className="text-mute-400 w-4" />} text={t("nav.sites")} to="/sites" />
                            <SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]} icon={<FontAwesomeIcon icon={faChargingStation} className="text-mute-400 w-4" />} text={t("nav.points")} to="/points" />
                            <SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]} icon={<FontAwesomeIcon icon={faPlug} className="text-mute-400 w-4" />} text={t("nav.connectors")} to="/connectors" />
                            <SidebarItem requiredRoles={[Roles.Admin, Roles.CpmsLight]} icon={<FontAwesomeIcon icon={faFileInvoice} className="text-mute-400 w-4" />} text={t("nav.tariffs")} to="/tariffs" />
                            <SidebarItem requiredRoles={[Roles.Admin]} icon={<FontAwesomeIcon icon={faIndustryWindows} className="text-mute-400 w-4" />} text={t("nav.manufacturers")} to="/manufacturers" platformOnly />
                        </SidebarItem>
                    </li>
                    {/* Relations */}
                    <li>
                        <SidebarItem
                            requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                            icon={<FontAwesomeIcon icon={faLinkHorizontal} className="text-mute-400 w-4" />}
                            text={t("nav.relations")}
                            to=""
                            expandOnly={true}
                            matching={[
                                "externalUser",
                                "partner",
                                "organization",
                                "customer",
                                "discount",
                                "token",
                                "accessGroup",
                            ]}
                        >
                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faUserGroup} className="text-mute-400 w-4" />}
                                text={t("nav.externalUsers")}
                                to="/externalUsers"
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faHandshake} className="text-mute-400 w-4" />}
                                text={t("nav.partners")}
                                to="/partners"
                                platformOnly
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faBuilding} className="text-mute-400 w-4" />}
                                text={t("nav.organizations")}
                                to="/organizations"
                                platformOnly
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                                icon={<FontAwesomeIcon icon={faFaceSmile} className="text-mute-400 w-4" />}
                                text={t("nav.customers")}
                                to="/customers"
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin]}
                                icon={<FontAwesomeIcon icon={faBadgeDollar} className="text-mute-400 w-4" />}
                                text={t("nav.discounts")}
                                to="/discounts"
                                platformOnly
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                                icon={<FontAwesomeIcon icon={faTag} className="text-mute-400 w-4" />}
                                text={t("nav.tokens")}
                                to="/tokens"
                            />

                            <SidebarItem
                                requiredRoles={[Roles.Admin, Roles.CpmsLight]}
                                icon={<FontAwesomeIcon icon={faKey} className="text-mute-400 w-4" />}
                                text={t("nav.accessGroups")}
                                to="/accessGroups"
                            />
                        </SidebarItem>
                    </li>


                    <li>
                        <SidebarItem requiredRoles={[Roles.Admin]}
                            icon={<FontAwesomeIcon icon={faGears} className="text-mute-400 w-4" />}
                            text={t("nav.systems")}
                            to=""
                            platformOnly
                            matching={[
                                "app-push",
                                "ocpi-parties",
                                "ocpi-party",
                                "systems",
                                "configurations",
                                "agent",
                            ]}
                            expandOnly={true}
                        >
                           <SidebarItem requiredRoles={[Roles.Admin, Roles.CustomerService]} icon={<FontAwesomeIcon icon={faComments} className="text-mute-400 w-4" />}text={t("nav.appPush")} to="/app-push"/>
                            <SidebarItem requiredRoles={[Roles.Admin]} icon={<FontAwesomeIcon icon={faFileInvoice} className="text-mute-400 w-4" />} text={t("nav.ocpiParties")} to="/ocpi-parties" />
                            <SidebarItem requiredRoles={[Roles.Admin]} icon={<FontAwesomeIcon icon={faComments} className="text-mute-400 w-4" />} text={t("nav.agge")} to="/agent" />
                            <SidebarItem requiredRoles={[Roles.Admin]} icon={<FontAwesomeIcon icon={faGear} className="text-mute-400 w-4" />} text={t("nav.integrations")} to="/systems" />
                            <SidebarItem requiredRoles={[Roles.Admin]} icon={<FontAwesomeIcon icon={faScrewdriverWrench} className="text-mute-400 w-4" />} text={t("nav.configurations")} to="/configurations" />
                        </SidebarItem>
                    </li>
                </ul>

            </aside>
        </div>
    );
}

function normalizePath(path) {
    return String(path || "").toLowerCase();
}

function detailPrefixFor(to) {
    const path = normalizePath(to).replace(/\/$/, "");

    if (path.endsWith("ies")) {
        return `${path.slice(0, -3)}y`;
    }

    if (path.endsWith("s") && path !== "/") {
        return path.slice(0, -1);
    }

    return path;
}

function pathEqualsOrIsNested(pathname, prefix) {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function menuKeyMatchesPath(pathname, match) {
    const needle = match.toLowerCase();
    return pathname
        .toLowerCase()
        .split("/")
        .filter(Boolean)
        .some((segment) => {
            if (segment === needle) {
                return true;
            }

            if (segment.startsWith(`${needle}-`)) {
                return false;
            }

            return segment.startsWith(needle);
        });
}

function routeMatches(pathname, to) {
    if (!to) {
        return false;
    }

    const current = normalizePath(pathname);
    const target = normalizePath(to);

    if (target === "/") {
        return current === "/";
    }

    if (pathEqualsOrIsNested(current, target)) {
        return true;
    }

    const detailPrefix = detailPrefixFor(to);
    return detailPrefix !== target && pathEqualsOrIsNested(current, detailPrefix);
}

export function SidebarItem({
    icon,
    text,
    alert,
    children,
    to,
    requiredRoles = [],
    matching = [],
    expandOnly = false,
    platformOnly = false,
}) {
    const { hasAnyRole } = useAuth();
    const actingAsOtherOrganization = Boolean(getActingOrganizationPublicId());
    const [isOpen, setIsOpen] = useState(false);
    const { toggleSidebar } = useSidebar();
    const [active, setActive] = useState(false);

    const location = useLocation();

    const syncOpenStateFromRoute = (pathname) => {
        if (!children) {
            if (routeMatches(pathname, to)) {
                setIsOpen(true);
            }
            return;
        }

        let shouldOpen = false;

        children?.props?.children?.forEach((child) => {
            if (routeMatches(pathname, child?.props?.to)) {
                shouldOpen = true;
            }
        });

        matching.forEach((match) => {
            if (menuKeyMatchesPath(pathname, match)) {
                shouldOpen = true;
            }
        });

        setIsOpen(shouldOpen);
    };

    useEffect(() => {
        const pathname = location.pathname;

        syncOpenStateFromRoute(pathname);

        if (expandOnly) {
            setActive(false);
            return;
        }

        setActive(routeMatches(pathname, to));
    // Only re-sync when the route changes — parent re-renders (e.g. grid-calculations filters) must not reset manual expand/collapse.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [location.pathname]);

    const handleClick = () => {
        if (window.innerWidth < 768) {
            toggleSidebar();
        }
    };

    if (requiredRoles && !hasAnyRole(requiredRoles)) {
        return null;
    }

    if (platformOnly && actingAsOtherOrganization) {
        return null;
    }

    return (
        <div>
            <NavLink
                to={to}
                onClick={(e) => {
                    if (expandOnly) {
                        e.preventDefault();
                        setIsOpen((prev) => !prev);
                    } else {
                        handleClick();
                    }
                }}
                className={`flex w-full rounded-lg ${
                    active && !expandOnly
                        ? "bg-primary-300/25 text-white [&_svg]:!text-primary-300"
                        : "bg-transparent text-white/90 hover:bg-white/10 [&_svg]:!text-primary-200"
                }`}
            >
                <div className="flex items-center justify-between py-2 px-3 w-full cursor-pointer group">
                    <div className="flex items-center">
                        <div className="w-5 mr-4 flex items-center justify-center">
                            <span>{icon}</span>
                        </div>
                        <span
                            className={`text-base ${
                                active && !expandOnly ? "font-medium text-white" : "font-normal text-white/90"
                            }`}
                        >
                            {text}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        {children && (
                            <FontAwesomeIcon
                                icon={isOpen ? faChevronDown : faChevronRight}
                                className="!text-white/50"
                            />
                        )}
                        {alert && <div className="w-2 h-2 rounded bg-primary-300" />}
                    </div>
                </div>
            </NavLink>

            {isOpen && children && (
                <ul className="pl-4">
                    {children}
                </ul>
            )}
        </div>
    );
}

